import './available-bank-products.scss';

import { Switch, notification } from 'antd';
import { useContext, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { PromotionModal } from '@/components/Shared/entities';
import { Button, CircledImage } from '@/components/Shared/ui';
import { BANKS_ICONS_NAMES, BankNames, DictTypes } from '@/constants';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import { CreateTabContext } from '../../../ExistedOrder';
import { EU_TARIFF_TYPES, EU_TARIFF_TYPE_TEXTS } from '../../../constants';
import { AdditionalProducts, StockBounds } from '../../entities';
import type { CreateAutoCreditProps } from '../../types/form-types';
import type { StockBoundElementType, TAddProducts } from '../../types/products';
import useAdditionalProducts from './hooks/useAdditionalProducts';
import useEurasProducts from './hooks/useEurasProducts';
import useGetStockBounds from './hooks/useGetStockBounds';
import useStockBoundChange from './hooks/useStockBoundChange';
import useStockBoundInfo from './hooks/useStockBoundInfo';

interface BankProductInterface {
  bankName: string;
  stockBounds: StockBoundElementType[];
  additionalProducts: TAddProducts[];
}

type BanksProductsType = Record<string, BankProductInterface>;

const AvailableBankProducts: React.FC = () => {
  const { dicts } = useAppSelector((state) => state.dict);
  const {
    userInfo,
    userAccesses: {
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);

  const { isFieldsFrozen } = useContext(CreateTabContext);

  const { control, setValue } = useFormContext<CreateAutoCreditProps>();
  const [stockBounds, additionalProducts, eurasianTariffType] = useWatch({
    control,
    name: ['stockBounds', 'additionalProducts', 'eurasianTariffType']
  });

  const { isLoading } = useGetStockBounds();
  const { initFeeModal, closeInitFeeModal, handleStockBoundChange } =
    useStockBoundChange();
  const { infoModalData, setInfoModalData, handleOpenInfoModal } =
    useStockBoundInfo();

  const {
    handleChangeProductInsurance,
    handleAddProductsChange,
    handleChangeAddProductLoanPeriod
  } = useAdditionalProducts();
  const { periodOptions } = useEurasProducts({
    handleChangeAddProductLoanPeriod
  });

  const eurasianTariffTypeOptions = useMemo(() => {
    return Object.entries(EU_TARIFF_TYPE_TEXTS)
      .filter(([value]) => {
        if (
          value !== EU_TARIFF_TYPES.PREFERENTIAL ||
          (isFieldsFrozen &&
            eurasianTariffType === EU_TARIFF_TYPES.PREFERENTIAL)
        )
          return true;
        return isAdmin || userInfo?.position.code === 3;
      })
      .map(([value, label]) => ({ value, label }));
  }, [userInfo, isAdmin, isFieldsFrozen, eurasianTariffType]);

  const banks = useMemo(() => {
    if (!stockBounds || !additionalProducts) return [];

    const banksMap: BanksProductsType = {};
    const length =
      stockBounds.length > additionalProducts.length
        ? stockBounds.length
        : additionalProducts.length;

    for (let i = 0; i < length; i++) {
      if (stockBounds[i]) {
        banksMap[stockBounds[i].bank] ??= {
          bankName: stockBounds[i].bank,
          stockBounds: [],
          additionalProducts: []
        };
        banksMap[stockBounds[i].bank]?.stockBounds.push(stockBounds[i]);
      }
      if (additionalProducts[i]) {
        banksMap[additionalProducts[i].bankName] ??= {
          bankName: additionalProducts[i].bankName,
          stockBounds: [],
          additionalProducts: []
        };
        banksMap[additionalProducts[i].bankName]?.additionalProducts.push(
          additionalProducts[i]
        );
      }
    }

    return Object.values(banksMap);
  }, [stockBounds, additionalProducts]);

  const euBank =
    dicts?.[DictTypes.Banks]?.getBy.name[safeKey(BankNames.euBankV2)];

  return (
    <div className="available-bank-products">
      <div className="available-bank-products__title">
        Доступные субсидии/акции банков
      </div>

      <div className="available-bank-products__content">
        {euBank && euBank.icon && (
          <div className="available-bank-products__content-item">
            <div className="available-bank-products__block">
              <div className="available-bank-products__bank">
                <CircledImage alt={euBank.name} src={euBank.icon} />
                {euBank.fullname}
              </div>
              <Controller
                control={control}
                name="eurasianTariffType"
                render={({ field }) => (
                  <>
                    {eurasianTariffTypeOptions.map((item) => {
                      const isChecked = field.value === item.value;
                      return (
                        <label
                          key={item.value}
                          className="available-bank-products__switch"
                        >
                          <Switch
                            checked={isChecked}
                            onChange={() => field.onChange(item.value)}
                            disabled={isFieldsFrozen}
                          />
                          <p>{item.label}</p>
                        </label>
                      );
                    })}
                  </>
                )}
              />
            </div>
          </div>
        )}

        {banks.map((item) => {
          const bank =
            dicts?.[DictTypes.Banks]?.getBy.name[safeKey(item.bankName)];
          const bankIconName =
            BANKS_ICONS_NAMES[item.bankName as keyof typeof BANKS_ICONS_NAMES];

          return (
            <div
              key={item.bankName}
              className="available-bank-products__content-item"
            >
              <div className="available-bank-products__block">
                <div className="available-bank-products__bank">
                  <CircledImage
                    alt={item.bankName}
                    src={bank?.icon || bankIconName?.icon}
                  />
                  {bank?.fullname || bankIconName?.name}
                </div>
                {item.stockBounds.length > 0 && (
                  <StockBounds
                    stockBounds={item.stockBounds}
                    setInfoModalData={handleOpenInfoModal}
                    isLoading={isLoading}
                    handleStockBoundChange={handleStockBoundChange}
                  />
                )}
              </div>
              {item.additionalProducts.length > 0 && (
                <div className="available-bank-products__block">
                  <AdditionalProducts
                    products={item.additionalProducts}
                    periodOptions={periodOptions}
                    handleAddProductsChange={handleAddProductsChange}
                    handleChangeProductInsurance={handleChangeProductInsurance}
                    handleChangeAddProductLoanPeriod={
                      handleChangeAddProductLoanPeriod
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <PromotionModal
        visible={!!infoModalData}
        onClose={() => setInfoModalData(null)}
        promotions={infoModalData ? [infoModalData] : []}
        selectedPromotion={infoModalData}
        setSelectedPromotion={setInfoModalData}
        showAcknowledgment={false}
      />

      {/* Корректировка первоначального взноса */}
      <CustomModal
        isModalOpen={!!initFeeModal}
        handleOk={() => {}}
        handleCancel={() => {
          handleStockBoundChange(initFeeModal?.stockUuid || '');
          closeInitFeeModal();
        }}
        title=""
        editClass="initial-payment-modal"
        footer={[
          <Button
            key="back"
            onClick={() => {
              handleStockBoundChange(initFeeModal?.stockUuid || '');
              closeInitFeeModal();
            }}
          >
            Закрыть
          </Button>,
          <Button
            key="submit"
            variant="filled-red"
            onClick={() => {
              setValue('initialPayment', initFeeModal?.value || '');
              closeInitFeeModal();
              notification.success({
                title: 'Сумма ПВ успешно изменена. Нажмите кнопку ПРОДОЛЖИТЬ',
                duration: 5
              });
            }}
          >
            Изменить
          </Button>
        ]}
      >
        <p className="initial-payment-modal__text">
          По выбранной акции от Евразийского банка сумма ПВ должна быть не менее{' '}
          <span className="initial-payment-modal__value">
            {initFeeModal?.value}
          </span>{' '}
          ₸
        </p>
      </CustomModal>
    </div>
  );
};

export default AvailableBankProducts;
